import { JSX } from 'react';
import {
    Box,
    Grid,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSharedTokenCost } from "./context/TokenCostContext";
import { TokenCostByModel, TokenCostTotals } from "../../../types/dashboard";
import { DashboardCard, DashboardCardError } from "./DashboardCard";
import { numeric, eyebrow, formatTokens, formatUsd, formatGbp, moneyPlaces } from "./styles/dashboard-tokens";

/**
 * What the platform's tokens cost.
 *
 * Money on a dashboard invites more trust than it has earned, so both cards here say what the
 * figure does not cover. Tokens recorded before the Operator started naming its model, and tokens
 * from a model with no price row, are reported as coverage gaps rather than folded into the total.
 */

/** Where somebody goes to fix a coverage gap. */
const PRICE_LIST_ROUTE: string = '/llm_model_prices';

const emptyTotals: TokenCostTotals = {
    usd: 0,
    gbp: 0,
    tokens: 0,
    pricedTokens: 0,
    unpricedTokens: 0,
    unattributedTokens: 0,
};

type SpendRowProps = {
    label: string,
    totals: TokenCostTotals,
}

/** One secondary period under the headline figure. */
const SpendRow: (props: SpendRowProps) => JSX.Element = (
    { label, totals }: SpendRowProps
): JSX.Element => {
    // Both figures are the same amount, so both are printed to the same precision. Letting each
    // decide would show "US$0.01" beside "£0.0091".
    const places: number = moneyPlaces(totals.usd);

    return (
        <Box
            sx={ {
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
                borderTop: 1,
                borderColor: 'divider',
                pt: 1.25,
                mt: 1.25,
            } }
        >
            <Typography component="span" sx={ eyebrow }>{ label }</Typography>
            <Box sx={ { display: 'flex', alignItems: 'baseline', gap: 1 } }>
                <Typography component="span" sx={ { ...numeric, fontSize: 15, fontWeight: 600 } }>
                    { formatUsd(totals.usd, places) }
                </Typography>
                <Typography component="span" sx={ { ...numeric, fontSize: 13 } } color="text.secondary">
                    { formatGbp(totals.gbp, places) }
                </Typography>
            </Box>
        </Box>
    );
}

type CoverageNoteProps = {
    totals: TokenCostTotals,
    unpricedModels: string[],
    rate: number,
}

/**
 * What the figure above does not cover.
 *
 * Renders nothing when everything is priced, so a healthy dashboard stays quiet. This is the part
 * that makes the money on this card honest: a total that silently excluded most of the traffic
 * would be worse than showing no total at all.
 */
const CoverageNote: (props: CoverageNoteProps) => JSX.Element | null = (
    { totals, unpricedModels, rate }: CoverageNoteProps
): JSX.Element | null => {
    const notes: JSX.Element[] = [];

    if (unpricedModels.length > 0) {
        notes.push(
            <Typography key="unpriced" variant="body2" color="text.secondary">
                { formatTokens(totals.unpricedTokens) + " tokens ran on a model with no price: " }
                { unpricedModels.join(', ') }
                { ". " }
                <Typography
                    component={ RouterLink }
                    to={ PRICE_LIST_ROUTE }
                    variant="body2"
                    sx={ { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } } }
                >
                    { "Add a price" }
                </Typography>
            </Typography>
        );
    }

    if (totals.unattributedTokens > 0) {
        notes.push(
            <Typography key="unattributed" variant="body2" color="text.secondary">
                { formatTokens(totals.unattributedTokens) + " tokens were recorded without a model and cannot be costed." }
            </Typography>
        );
    }

    if (rate === 0) {
        notes.push(
            <Typography key="rate" variant="body2" color="text.secondary">
                { "No USD to GBP rate is set, so the GBP figures read zero." }
            </Typography>
        );
    }

    if (notes.length === 0) {
        return null;
    }

    return (
        <Box sx={ { mt: 'auto', pt: 2, display: 'flex', flexDirection: 'column', gap: 0.75 } }>
            <Typography component="p" sx={ eyebrow }>{ "Not covered" }</Typography>
            { notes }
        </Box>
    );
}

export const TokenCostSummary: () => JSX.Element = (): JSX.Element => {
    const { data, loading, error, reloadData } = useSharedTokenCost();

    function handleRefresh() {
        reloadData();
    }

    const refreshAction: JSX.Element = (
        <Tooltip title="Reload cost">
            <IconButton aria-label="Reload cost" size="small" onClick={ handleRefresh }>
                <ReplayIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const today: TokenCostTotals = data?.today ?? emptyTotals;
    const yesterday: TokenCostTotals = data?.yesterday ?? emptyTotals;
    const monthToDate: TokenCostTotals = data?.monthToDate ?? emptyTotals;
    const byModel: TokenCostByModel[] = data?.byModel ?? [];
    const unpricedModels: string[] = data?.unpricedModels ?? [];
    const rate: number = data?.usdToGbpRate ?? 0;

    // Today's spend, stated plainly. GBP sits beside it rather than replacing it, because the
    // dollar figure is what the provider invoice will say.
    const spendMeta: JSX.Element = (
        <Box sx={ { display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' } }>
            <Typography component="span" sx={ { ...numeric, fontSize: 26, fontWeight: 600, lineHeight: 1 } }>
                { formatUsd(today.usd, moneyPlaces(today.usd)) }
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
                { formatGbp(today.gbp, moneyPlaces(today.usd)) + " today" }
            </Typography>
        </Box>
    );

    const headCell = { ...eyebrow, borderBottom: 1, borderColor: 'divider', py: 1 };

    return (
        <Grid container spacing={ 2 }>
            <Grid size={ { xs: 12, md: 5 } }>
                <DashboardCard
                    eyebrow="Cost"
                    title="Estimated spend"
                    actions={ refreshAction }
                    meta={ !loading && error === null ? spendMeta : undefined }
                    busy={ loading }
                >
                    { loading &&
                        <>
                            <Skeleton variant="rounded" animation="wave" height={ 120 } />
                            <Skeleton variant="text" animation="wave" sx={ { mt: 2 } } />
                        </>
                    }
                    { (!loading && error !== null) &&
                        <DashboardCardError
                            message={ "Cost unavailable — " + error }
                            onRetry={ handleRefresh }
                        />
                    }
                    { (!loading && error === null) &&
                        <>
                            <SpendRow label="Yesterday" totals={ yesterday } />
                            { /* All time is not here. It sits beside the all-time token count on
                                 the budget card, where the figure it belongs to already is. */ }
                            <SpendRow label="Month to date" totals={ monthToDate } />
                            <CoverageNote
                                totals={ monthToDate }
                                unpricedModels={ unpricedModels }
                                rate={ rate }
                            />
                        </>
                    }
                </DashboardCard>
            </Grid>
            <Grid size={ { xs: 12, md: 7 } }>
                <DashboardCard
                    eyebrow="Cost"
                    title="Spend by model, month to date"
                    actions={ refreshAction }
                    busy={ loading }
                    clip
                >
                    { loading &&
                        <Skeleton variant="rounded" animation="wave" height={ 280 } />
                    }
                    { (!loading && error !== null) &&
                        <DashboardCardError
                            message={ "Cost unavailable — " + error }
                            onRetry={ handleRefresh }
                        />
                    }
                    { (!loading && error === null && byModel.length === 0) &&
                        <Box sx={ { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
                            <Typography variant="body2" color="text.secondary">
                                { "No token usage recorded this month yet." }
                            </Typography>
                        </Box>
                    }
                    { (!loading && error === null && byModel.length > 0) &&
                        <TableContainer sx={ { maxHeight: 300 } }>
                            <Table stickyHeader size="small" aria-label="Spend by model">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={ headCell }>{ "Model" }</TableCell>
                                        <TableCell sx={ headCell }>{ "Provider" }</TableCell>
                                        <TableCell sx={ { ...headCell, width: 110 } } align="right">{ "Tokens" }</TableCell>
                                        <TableCell sx={ { ...headCell, width: 100 } } align="right">{ "USD" }</TableCell>
                                        <TableCell sx={ { ...headCell, width: 100 } } align="right">{ "GBP" }</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { byModel.map((row: TokenCostByModel): JSX.Element => {
                                        return (
                                            <TableRow key={ row.model } sx={ { '&:hover': { bgcolor: 'action.hover' } } }>
                                                <TableCell sx={ { fontWeight: 500 } }>
                                                    { row.label }
                                                </TableCell>
                                                <TableCell sx={ { textTransform: 'capitalize' } }>
                                                    { row.provider !== '' ? row.provider :
                                                        <Typography component="span" variant="body2" color="text.disabled">
                                                            { "—" }
                                                        </Typography>
                                                    }
                                                </TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">
                                                    { formatTokens(row.tokens) }
                                                </TableCell>
                                                { /* A model with no price row shows a dash rather than a zero. Zero is
                                                     a real answer for local Ollama, and the two must not read alike. */ }
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">
                                                    { row.priced ? formatUsd(row.usd, moneyPlaces(row.usd)) :
                                                        <Typography component="span" variant="body2" color="text.disabled">
                                                            { "not priced" }
                                                        </Typography>
                                                    }
                                                </TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">
                                                    { row.priced ? formatGbp(row.gbp, moneyPlaces(row.usd)) :
                                                        <Typography component="span" variant="body2" color="text.disabled">
                                                            { "—" }
                                                        </Typography>
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </DashboardCard>
            </Grid>
        </Grid>
    );
}
